<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import ReportsList from '@/components/report/ReportsList.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const reportUuid = computed(() => route.params.report_uuid)

// Get data from Vuex store
const reports = computed(() => {
  return store.getters['report/getOrgReports'](orgId.value)
})

const ALL_PROJECTS = 'all'
const selectedProjectId = ref(ALL_PROJECTS)

const projectOptions = computed(() => {
  const seen = new Map()
  for (const r of reports.value) {
    if (r.project_id && !seen.has(r.project_id)) {
      seen.set(r.project_id, r.project_name || `Project ${r.project_id}`)
    }
  }
  return Array.from(seen, ([id, name]) => ({ id, name }))
})

const filteredReports = computed(() => {
  if (selectedProjectId.value === ALL_PROJECTS) return reports.value
  return reports.value.filter((r) => String(r.project_id) === selectedProjectId.value)
})

const currentReport = computed(() => {
  if (!reportUuid.value) return null
  return store.getters['report/getReport'](orgId.value, reportUuid.value)
})

// Select a report - navigate to it
const selectReport = (report) => {
  console.log('Selecting report:', report)
  store.dispatch('report/setActiveReport', report.report_uuid)
  store.dispatch('report/setActiveVersion', null) // Reset to latest version
  router.push({
    name: 'ReportDetail',
    params: {
      org_id: orgId.value,
      report_uuid: report.report_uuid
    }
  })
}

// Delete report
const deleteReport = async (report) => {
  console.log('Deleting report:', report)

  try {
    await store.dispatch('report/deleteReportAction', {
      orgId: orgId.value,
      reportUuid: report.report_uuid
    })

    toast({
      title: 'Deleted',
      description: 'Report deleted',
      variant: 'default'
    })

    // If we deleted the current report, navigate back to list
    if (reportUuid.value === report.report_uuid) {
      router.push({ name: 'OrgReportsPage', params: { org_id: orgId.value } })
    }
  } catch (error) {
    console.error('Error deleting report:', error)
    toast({
      title: 'Error',
      description: 'Failed to delete report',
      variant: 'destructive'
    })
  }
}

// Fetch reports on mount
onMounted(async () => {
  console.log('Reports mounted, fetching reports for org:', orgId.value)

  try {
    await store.dispatch('report/fetchReports', { orgId: orgId.value })
  } catch (error) {
    console.error('Error fetching reports:', error)
    toast({
      title: 'Error',
      description: 'Failed to load reports',
      variant: 'destructive'
    })
  }
})
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="px-2 pb-2 border-b border-border flex-shrink-0 space-y-2">
      <h2 class="ml-3 text-sm font-semibold py-1">Reports</h2>
      <div v-if="projectOptions.length" class="px-1">
        <Select v-model="selectedProjectId">
          <SelectTrigger class="h-8 text-xs">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem :value="ALL_PROJECTS">All projects</SelectItem>
              <SelectItem
                v-for="p of projectOptions"
                :key="p.id"
                :value="String(p.id)"
              >
                {{ p.name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
    <ReportsList
      :reports="filteredReports"
      :active-report="currentReport"
      @select="selectReport"
      @delete="deleteReport"
      class="flex-1 overflow-y-auto"
    />
  </div>
</template>
